import React from "react";
import {
  Box,
  Typography,
  Stack,
  Divider,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormGroup,
  Checkbox,
} from "@mui/material";
import { SparePiece } from "react-chessboard";
import { CastlingRights, WHITE_SPARE_PIECES, BLACK_SPARE_PIECES } from "./types";

interface EditControlsProps {
  editTurn: "w" | "b";
  editCastling: CastlingRights;
  onEditTurnChange: (newTurn: "w" | "b") => void;
  onEditCastlingChange: (key: keyof CastlingRights, checked: boolean) => void;
}

export const EditControls: React.FC<EditControlsProps> = ({
  editTurn,
  editCastling,
  onEditTurnChange,
  onEditCastlingChange,
}) => {
  return (
    <Box
      sx={{
        mt: 2.5,
        p: 2,
        borderRadius: 2,
        bgcolor: "rgba(0, 0, 0, 0.3)",
        border: "1px dashed rgba(255, 255, 255, 0.2)",
      }}
    >
      <Typography variant="caption" color="grey.400" sx={{ display: "block", mb: 1 }}>
        SPARE PIECES (Drag onto board to place, or drag board pieces off-board to delete):
      </Typography>

      {/* Row 1: White Pieces */}
      <Stack direction="row" spacing={1.5} justifyContent="center" alignItems="center" mb={1.5}>
        <Typography variant="caption" sx={{ minWidth: 48, color: "grey.300", fontWeight: 600 }}>
          White:
        </Typography>
        {WHITE_SPARE_PIECES.map((pieceType) => (
          <Box
            key={pieceType}
            sx={{
              width: 44,
              height: 44,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "grab",
              bgcolor: "rgba(255, 255, 255, 0.08)",
              borderRadius: 1.5,
              border: "1px solid rgba(255, 255, 255, 0.15)",
              transition: "all 0.15s ease",
              "&:hover": {
                bgcolor: "rgba(255, 255, 255, 0.2)",
                transform: "scale(1.08)",
              },
            }}
          >
            <SparePiece pieceType={pieceType} />
          </Box>
        ))}
      </Stack>

      {/* Row 2: Black Pieces */}
      <Stack direction="row" spacing={1.5} justifyContent="center" alignItems="center">
        <Typography variant="caption" sx={{ minWidth: 48, color: "grey.300", fontWeight: 600 }}>
          Black:
        </Typography>
        {BLACK_SPARE_PIECES.map((pieceType) => (
          <Box
            key={pieceType}
            sx={{
              width: 44,
              height: 44,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "grab",
              bgcolor: "rgba(255, 255, 255, 0.08)",
              borderRadius: 1.5,
              border: "1px solid rgba(255, 255, 255, 0.15)",
              transition: "all 0.15s ease",
              "&:hover": {
                bgcolor: "rgba(255, 255, 255, 0.2)",
                transform: "scale(1.08)",
              },
            }}
          >
            <SparePiece pieceType={pieceType} />
          </Box>
        ))}
      </Stack>

      <Divider sx={{ my: 2, borderColor: "rgba(255, 255, 255, 0.1)" }} />

      {/* Edit Position Settings: Turn and Castling */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        gap={2}
      >
        <FormControl component="fieldset" size="small">
          <FormLabel component="legend" sx={{ color: "grey.400", fontSize: "0.8rem" }}>
            Side to Move
          </FormLabel>
          <RadioGroup
            row
            value={editTurn}
            onChange={(e) => onEditTurnChange(e.target.value as "w" | "b")}
          >
            <FormControlLabel
              value="w"
              control={<Radio size="small" sx={{ color: "grey.400" }} />}
              label={<Typography variant="body2">White</Typography>}
            />
            <FormControlLabel
              value="b"
              control={<Radio size="small" sx={{ color: "grey.400" }} />}
              label={<Typography variant="body2">Black</Typography>}
            />
          </RadioGroup>
        </FormControl>

        <Box>
          <Typography variant="caption" color="grey.400" sx={{ display: "block", mb: 0.5 }}>
            Castling Rights
          </Typography>
          <FormGroup row>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={editCastling.K}
                  onChange={(e) => onEditCastlingChange("K", e.target.checked)}
                  sx={{ color: "grey.400" }}
                />
              }
              label={<Typography variant="caption">White O-O</Typography>}
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={editCastling.Q}
                  onChange={(e) => onEditCastlingChange("Q", e.target.checked)}
                  sx={{ color: "grey.400" }}
                />
              }
              label={<Typography variant="caption">White O-O-O</Typography>}
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={editCastling.k}
                  onChange={(e) => onEditCastlingChange("k", e.target.checked)}
                  sx={{ color: "grey.400" }}
                />
              }
              label={<Typography variant="caption">Black O-O</Typography>}
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={editCastling.q}
                  onChange={(e) => onEditCastlingChange("q", e.target.checked)}
                  sx={{ color: "grey.400" }}
                />
              }
              label={<Typography variant="caption">Black O-O-O</Typography>}
            />
          </FormGroup>
        </Box>
      </Stack>
    </Box>
  );
};

export default EditControls;
